/**
 * Legacy global window shim — M2.9.
 *
 * Installs `window.EY_RAPToR_*` and a few non-prefixed aliases so MFEs
 * (and the legacy Angular host during canary) keep functioning while we
 * rewrite the shell in React.
 *
 * Spec: .docs/legacy-globals-matrix.md (authoritative — every row below is
 * meant to map 1:1 to a row in §1).
 *
 * Strategy:
 *  - Globals backed by a real React-shell implementation are bound to live
 *    getters / function references.
 *  - Globals whose React equivalent isn't built yet are wired to a tracked
 *    stub that emits `legacy_global_unbound` telemetry. This keeps the
 *    surface area complete (no `undefined is not a function`) and gives us
 *    the dashboard we need for D7 deprecation.
 *  - Every call also emits a `legacy_global_called` event so we can measure
 *    real usage and shrink the surface ahead of T+30-day sunset.
 *
 * Sunset target (D7): after 100% canary rollout, every binding here throws
 * with a link to the migration guide.
 */

import type { Subscription } from './legacy-rxjs-shim';
import { observableFromPromise } from './legacy-rxjs-shim';
import { getPortalMessageObservable } from './signal-r';
import { getRxJs } from './rxjs-loader';
import { authModule, onTokenRotation, onLogout } from '@/auth/auth-module';
import { getAuthProvider, resolveClientIdStrict } from '@/auth/auth-provider';
import { acquireMfeTokenAcrossClientIds } from '@/auth/multi-client-token';
import { getRumToken, clearRumTokenCache } from '@/auth/rum-token';
import { getCurrentUserType } from '@/auth/user-type';
import { useClientStore, getActiveClientId } from '@/client/client-store';
import { fullPortalRefresh } from '@/client/refresh-client-context';
import { displayLoader, hideLoader } from '@/ui/loader-store';
import { eventsModule } from '@/events/events-module';
import { scopesForUrl } from '@/http/scope-map';
import { triggerUrlClientChange } from '@/client/url-client-change';
import { navigateBrowserToLegacyTarget } from '@/router/legacy-route-adapter';
import { openPopupMfe, closePopupMfe } from '@/mfe/popup-store';
import { pushRouteState, getPreviousURL } from '@/router-state/route-state';
import { isEnvFlagTrue } from '@/config/env-flags';

interface LegacyEventCallback {
  (detail: unknown): void;
}

let installed = false;

function stub<T>(_name: string, returnValue: T): () => T {
  return () => returnValue;
}

/**
 * Receive-event registry. Mirrors `eventbus.service.ts:12`: same eventType
 * can have multiple callbacks; legacy uses CustomEvent on window, so we
 * delegate to `window.addEventListener` for cross-shell parity.
 */
function bindReceiveEvent(eventType: string, callback: LegacyEventCallback): void {
  const handler = (e: Event) => callback((e as CustomEvent).detail);
  window.addEventListener(eventType, handler);
}

function emitEvent(eventType: string, detail: unknown): void {
  window.dispatchEvent(new CustomEvent(eventType, { detail }));
}

// RUM-token acquisition + cache now lives in `@/auth/rum-token` so it can be
// shared with `http()` (which must attach a `Raptor-Authorization` header on
// RUM-protected APIM endpoints). `getRumToken` / `clearRumTokenCache` are
// imported above.

export function installLegacyGlobals(): void {
  if (installed) return;
  installed = true;

  // Invalidate cached RUM tokens whenever the active client rotates (legacy
  // `clearRumTokenCache()` on client change). Keeps us from serving a RUM
  // token bound to the previous client after a switch.
  onTokenRotation(clearRumTokenCache);

  // Also clear the RUM-token cache on logout so it can't leak into the next
  // session (legacy clears the RUM cache on session events).
  onLogout(clearRumTokenCache);

  const w = window as unknown as Record<string, unknown>;

  // ---- Auth / Login ----------------------------------------------------
  w.EY_RAPToR_logOutUser = (_broadcastSession?: boolean) => {
    authModule.logout();
  };

  // Tokens — HIGH-RISK surface; gated by deprecation telemetry.
  // Legacy `getAccessTokenforMFE(scopes, clientId?)` acquires a token across
  // the primary + additional app registrations (enableSingleTenantAuth). We
  // replicate that so MFE scopes the primary app can't mint (e.g. the
  // notification-preferences service) resolve against the right registration
  // instead of coming back 401.
  const getAccessTokenforMFE = (scopes: string[] = [], clientId?: string) => {
    // Route-scoped provider (PRTL-2857): when the CURRENT route is an Auth0 MFE,
    // mint the token via Auth0 (`paramclientid` = passed clientId, else strictly
    // resolved) so the tenant Action can enrich `user-roles`/`clientId`. Every
    // other route keeps the multi-registration MSAL path unchanged.
    const provider = getAuthProvider();
    let tokenPromise: Promise<string>;
    if (provider === 'auth0') {
      const paramclientid = clientId || resolveClientIdStrict() || '';
      tokenPromise = paramclientid
        ? authModule.getTokenAsync(scopes, { provider: 'auth0', paramclientid })
        : Promise.reject(new Error('Auth0 MFE token acquisition requires a resolved client id.'));
    } else {
      tokenPromise = acquireMfeTokenAcrossClientIds(scopes, clientId);
    }
    const rxjs = getRxJs();
    if (rxjs) return rxjs.from(tokenPromise);
    return observableFromPromise(tokenPromise);
  };
  w.EY_RAPToR_getAccessTokenforMFE = getAccessTokenforMFE;
  // Non-prefixed alias from §2 — same impl, separate name.
  w.getAccessTokenforMFE = getAccessTokenforMFE;

  w.EY_RAPToR_getScopesForEndPoint = (url: string) => {
    return scopesForUrl(url);
  };

  w.EY_RAPToR_UserType = () => {
    return getCurrentUserType();
  };
  w.EY_RAPToR_getUserOID = () => {
    return authModule.user?.oid ?? '';
  };
  w.EY_RAPToR_getIssuer = () => {
    return authModule.user?.issuer ?? '';
  };
  w.EY_RAPToR_getUserPrincipalName = () => {
    return authModule.user?.principalName ?? '';
  };
  w.EY_RAPToR_getUserEmail = () => {
    return authModule.user?.email ?? '';
  };
  w.EY_RAPToR_getMSALIdToken = () => {
    return authModule.getIdToken() ?? '';
  };
  w.EY_RAPToR_Username = () => {
    return authModule.user?.name ?? authModule.user?.email ?? '';
  };
  w.EY_RAPToR_IsSingleTenantAuth = () => {
    // The React shell is internal-tenant-only (no external/dual-tenant path), so
    // single-tenant auth is always ON — matching legacy QA/prod
    // `enableSingleTenantAuth: true`. MFEs (e.g. the workspace `ADInterceptor`)
    // gate token-scope selection on this: `true` keeps them on the INTERNAL API
    // consent scope. The prior `false` stub sent them down the `ey.login.tnnt`
    // fallback, which for external users selects an EXTERNAL scope no internal
    // registration can mint — a 400 on `/oauth2/v2.0/token` and no token.
    return true;
  };
  w.EY_RAPToR_IsSessionStorageEnabled = () => {
    // Legacy `raptor-msal.service.ts:isSessionStorageEnabled()` returns the
    // `enableSessionStorage` flag value — NOT merely "does sessionStorage
    // exist". MFEs (e.g. admin `getRoleMapping`) call this to decide whether to
    // read `currentUser` from session vs local storage, so it MUST agree with
    // where `legacy-user-bridge.write()` puts it. If they disagree the MFE reads
    // an empty store and throws on its own invalid-JSON fallback
    // (`'{ isGlobal : false}'`).
    return isEnvFlagTrue(import.meta.env.VITE_ENABLE_SESSION_STORAGE);
  };

  // ---- Loader ----------------------------------------------------------
  w.EY_RAPToR_displayLoader = (message?: string) => {
    emitEvent('EY.RAPToR.App.Loader.Running', 'true');
    displayLoader(message);
  };
  w.EY_RAPToR_hideLoader = () => {
    emitEvent('EY.RAPToR.App.Loader.Running', 'false');
    hideLoader();
  };

  // ---- Side navigation status ------------------------------------------
  // Legacy `side-navigation.component.ts` exposes
  // `window.EY_RAPToR_getSideNavStatus` (returns the rail state string —
  // 'Closed' | 'Collapsed' | 'Expanded', the GlobalNavigationRailState enum)
  // and emits 'EY.RAPToR.App.sidenavstatus' on every change. Deployed MFEs
  // (e.g. DATA / PIE) call it as a BARE global inside `ngOnInit`
  // (`shellSideNavVisibilityListener`) and subscribe to the event to position
  // their own content. Without it the bare reference throws
  // `ReferenceError: EY_RAPToR_getSideNavStatus is not defined` on every
  // change-detection tick -> Angular error loop, AppInsights spam, and the
  // visible screen-jumping the user reported.
  //
  // Mirrors the legacy `GlobalNavigationRailState`. The header hamburger toggles
  // VISIBILITY (`shell:toggle-sidenav`): hidden -> 'Closed', shown -> its expand
  // state. The rail's bottom chevron toggles EXPAND (`shell:expand-sidenav`):
  // 'Collapsed' (60px) <-> 'Expanded' (labeled panel). Re-broadcast the legacy
  // event on every change so deployed MFEs reposition their content.
  let railVisible = true;
  let railExpanded = false;
  const computeSideNavStatus = (): 'Closed' | 'Collapsed' | 'Expanded' =>
    !railVisible ? 'Closed' : railExpanded ? 'Expanded' : 'Collapsed';
  let sideNavStatus = computeSideNavStatus();
  w.EY_RAPToR_getSideNavStatus = () => {
    return sideNavStatus;
  };
  eventsModule.on('shell:toggle-sidenav', () => {
    railVisible = !railVisible;
    sideNavStatus = computeSideNavStatus();
    emitEvent('EY.RAPToR.App.sidenavstatus', sideNavStatus);
  });
  eventsModule.on('shell:expand-sidenav', () => {
    railExpanded = !railExpanded;
    sideNavStatus = computeSideNavStatus();
    emitEvent('EY.RAPToR.App.sidenavstatus', sideNavStatus);
  });

  // ---- Event bus -------------------------------------------------------
  w.EY_RAPToR_emitEvent = (eventType: string, detail: unknown) => {
    emitEvent(eventType, detail);
  };
  w.EY_RAPToR_receiveEvent = (eventType: string, cb: LegacyEventCallback) => {
    bindReceiveEvent(eventType, cb);
  };

  // ---- Client ----------------------------------------------------------
  w.EY_RAPToR_getCurrentClientId = () => {
    return getActiveClientId() ?? '';
  };

  // Friendly name of the active client. Legacy MFEs (e.g. STF's
  // `fetchClientDetails`) call this; parity with
  // `raptor-shell-header/header.service.ts:getClientName` which returns the
  // selected client's `clientName`.
  w.EY_RAPToR_getClientName = () => {
    return useClientStore.getState().current?.name ?? '';
  };

  // Returns the legacy `currentUser` blob (matches the shape written by
  // `legacy-user-bridge.ts`). Legacy MFEs use this when reading the value
  // through a function rather than `sessionStorage.getItem('currentUser')`
  // directly.
  w.EY_RAPToR_getCurrentUser = () => {
    try {
      const raw = sessionStorage.getItem('currentUser') ?? localStorage.getItem('currentUser');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  w.EY_RAPToR_URLClientChange = (clientId: string) => {
    return observableFromPromise(triggerUrlClientChange(clientId));
  };
  w.EY_RAPToR_URLClientRefreshInTabs = (clientId: string) => {
    return observableFromPromise(triggerUrlClientChange(clientId));
  };

  w.EY_RAPToR_IsClientSwitchPopUpOpen = () => {
    return useClientStore.getState().isSwitchPopupOpen;
  };

  w.EY_RAPToR_resetClientSelection = () => {
    useClientStore.getState().resetToPrevious();
  };

  // ---- Portal refresh / broadcast hooks --------------------------------
  // Full Angular parity: mirrors `refreshClientList()` + the side-navigation
  // `CLIENT.REFRESH` handler chain. Clears caches, re-resolves roles/screens,
  // refreshes the client dropdown, validates screen access, and remounts MFEs.
  w.EY_RAPToR_PortalRefresh = () => {
    // TEMP DIAGNOSTIC: confirm the orchestrator/header actually invokes this on
    // a different-client task start (remove once verified).

    void fullPortalRefresh();
  };

  // ---- RUM / Portal token ----------------------------------------------
  // Legacy MFEs call `EY_RAPToR_getRumToken(clientId, 'Portal')` and expect
  // a real RxJS Observable that emits the RUM `raptorToken` returned by
  // `/rum/tokenservice/v1/authorization/token`. This token is then sent to
  // widget APIs in `Raptor-Authorization`.
  w.EY_RAPToR_getRumToken = (_clientId?: string, scopes?: string | string[]) => {
    const rxjs = getRxJs();
    const tokenPromise = getRumToken(_clientId, scopes);
    if (rxjs) return rxjs.from(tokenPromise);
    return observableFromPromise(tokenPromise);
  };
  // SignalR observable — real-time portal push channel. Legacy MFEs subscribe
  // to this for live role/client/incident updates. Backed by the SignalR
  // service's multicast subject (see `@/legacy/signal-r`); the connection is
  // started in `main.tsx` once an MSAL account is active.
  w.EY_RAPToR_portalSignalRObservable = getPortalMessageObservable();
  // Route-state — wired in M2.10.
  w.EY_RAPToR_pushRouteState = (nav: { currentRoute: string; fullRoute: string }) => {
    pushRouteState(nav);
  };
  w.EY_RAPToR_getPreviousURL = () => {
    return getPreviousURL();
  };

  // ---- Cross-MFE routing -----------------------------------------------
  // Legacy `RouteStateService` (raptor-shell-ui route-state.service.ts) binds
  // these on `window`. Deployed MFEs (the embedded portal header's app/teams
  // tiles, "Notification Preferences", chatbot, etc.) call the BARE `routeToMFE`
  // global directly — without it, clicking a tile throws
  // `ReferenceError: routeToMFE is not defined` and the MFE's global error
  // handler spams. Legacy `routeToMFE(url)` navigates to
  // `/{clientId}/app-container/{url}`; the React shell mounts MFEs at
  // `/app/{appId}`, so we route through `navigateBrowserToLegacyTarget`, the
  // same adapter the header bridge uses (maps a legacy target to the canonical
  // `/app/{appId}` path and drives the router via history + popstate).
  const routeToMfe = (url: string) => {
    if (typeof url === 'string' && url.length > 0) navigateBrowserToLegacyTarget(url);
  };
  w.routeToMFE = routeToMfe;
  w.EY_RAPToR_routeToMFE = (url: string) => {
    if (typeof url === 'string' && url.length > 0) navigateBrowserToLegacyTarget(url);
  };
  // Legacy `routeFromMFEToMFEWithDynamicClient(url, clientId)` switches the
  // active client first, then navigates. Mirror that: rotate the client (which
  // persists storage + forces token refresh) before routing.
  w.EY_RAPToR_routeToMFE_WithDynamicClient = (url: string, clientId: string) => {
    if (clientId) void triggerUrlClientChange(clientId);
    if (typeof url === 'string' && url.length > 0) navigateBrowserToLegacyTarget(url);
  };
  // Legacy `routeToPopUpMFE(url)` navigates the named `popup` outlet to
  // `apps/<url>`. The React shell mounts popup MFEs in `PopupMfeOverlay`, so
  // open the overlay for the requested app id. `url` may be `STF`, `apps/STF`,
  // or `apps/STF/contact-support` — take the first segment after `apps/`.
  // An empty/falsy `url` mirrors Angular navigating the popup outlet to `null`
  // (`{ outlets: { popup: null } }`), which closes the active popup — e.g. the
  // orchestrator window a host MFE dismisses via `routeToPopUpMFE('')`.
  w.EY_RAPToR_routeToPopUpMFE = (url: string) => {
    const normalized = String(url ?? '')
      .replace(/^\/+/, '')
      .replace(/^apps\//i, '');
    const segments = normalized.split('/').filter(Boolean);
    const id = segments[0];
    if (id) {
      const subPath = segments.slice(1).join('/') || undefined;
      openPopupMfe(id, subPath);
    } else {
      closePopupMfe();
    }
  };

  // refreshClients — bound via shell:refresh-clients event; useClients hook
  // listens and invalidates the query (force=true bypasses staleTime).
  w.EY_RAPToR_refreshClients = (force?: boolean) => {
    eventsModule.emit('shell:refresh-clients', { force: Boolean(force) });
  };
  // Close the active popup MFE overlay (legacy `closeMFEModalPopup`).
  w.EY_RAPToR_closeMFEModalPopup = () => {
    closePopupMfe();
  };
  // Dirty-form flag — defaults to false; MFEs may overwrite when needed.
  w.EY_RAPToR_IsEditedInMFE = stub('EY_RAPToR_IsEditedInMFE', false);
}

// Re-export the rxjs-shim Subscription type so legacy callers that import it
// from the shell (rare but documented) still find it.
export type { Subscription };
